// BHP-1064: invoice read hooks over Backend2.0 (Hydra), replacing the legacy
// `InvoiceService` on `/api/v1/{customerUuid}/invoice`.
//
// The collection is customer-scoped by the backend (BHP-1049), so there is no
// customer uuid in any of these calls and nothing to filter client-side. The
// old useRecentInvoices() trimmed the list to the last three months in the
// browser because there was no pagination; there is now, so the overview shows
// everything, newest first — the default order lives on the backend resource
// (BHP-1064), so no sort parameter is sent from here.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCollection, useItem } from '../../../shared/api/useHydra';
import { type HydraCollection } from '../../../shared/api/hydra';
import { type Invoice, type InvoiceSpecification } from '../invoice';
import { ApiError } from '../../../shared/http/ApiError';
import { type ApiFile, httpClient } from '../../../shared/http/httpClient';

/**
 * BHP-1160: a finalized document never changes, and it is measured in megabytes.
 *
 * The shared client defaults (30 s stale, retry once) are wrong on both counts
 * here: every tab switch more than half a minute apart would re-download the
 * whole PDF, and because that yields a new Blob and a new object URL the frame
 * reloads and the reader loses their place. A 4xx is a correct answer about
 * this document — retrying it only delays the message.
 *
 * BHP-1144: a transport failure is now an ApiError with status 0 (it used to
 * be a raw TypeError). Settled answers are not retried: a real 4xx, and an
 * invalid body (a WAF/proxy page that would come back the same). Status 0 and
 * 5xx are transient. Keying on the 400–499 band rather than `< 500` keeps
 * status 0 out, which would otherwise silently lose the one retry a dropped
 * connection deserves.
 */
export function shouldRetryDocument(
  failureCount: number,
  error: Error
): boolean {
  const isSettled =
    error instanceof ApiError &&
    (error.kind === 'invalid-body' ||
      (error.status >= 400 && error.status < 500));

  return !isSettled && failureCount < 1;
}

const DOCUMENT_QUERY = {
  staleTime: Infinity,
  gcTime: 15 * 60 * 1000,
  retry: shouldRetryDocument
} as const;

export function useInvoices(
  page = 1
): UseQueryResult<HydraCollection<Invoice>> {
  return useCollection<Invoice>('invoices', { page });
}

export function useInvoice(uuid: string | null): UseQueryResult<Invoice> {
  return useItem<Invoice>('invoices', uuid ?? '', { enabled: uuid !== null });
}

/*
 * BHP-1160: useInvoiceLines() used to live here, for the line table on the
 * detail page. The page shows the PDF Exact Online issued instead, so nothing
 * in the portal renders lines any more and the hook went with it. The
 * `/api/invoice_lines` endpoint and its `invoice.uuid` filter (BHP-1064) are
 * untouched — the admin screen (BHP-1157/1163) is their next consumer, and it
 * will come with its own read model rather than inherit a hook nobody calls.
 */

/**
 * BHP-1160: the specifications of one invoice, oldest month first (the backend
 * resource sets that order). Same construction as the lines above.
 */
export function useInvoiceSpecifications(
  invoiceUuid: string | null
): UseQueryResult<HydraCollection<InvoiceSpecification>> {
  return useCollection<InvoiceSpecification>('invoice_specifications', {
    params: { 'invoice.uuid': invoiceUuid ?? '' },
    enabled: invoiceUuid !== null
  });
}

/**
 * BHP-1160: a document as a Blob, so it can be rendered in the page.
 *
 * It has to travel through the HTTP client rather than straight into an
 * `<iframe src>`: the endpoint needs the Bearer token, and a browser sends no
 * Authorization header when it loads a subresource. The caller turns this into
 * an object URL and is responsible for revoking it.
 *
 * `enabled` is the caller's way of not asking for a document that the invoice
 * already said is not there — a 404 is a correct answer, not a reason to retry.
 */
export function useInvoiceDocument(
  invoiceUuid: string | null,
  enabled: boolean
): UseQueryResult<ApiFile> {
  return useQuery({
    queryKey: ['invoice-document', invoiceUuid],
    queryFn: async () =>
      await httpClient.getFile(`/invoices/${invoiceUuid ?? ''}/document`),
    enabled: invoiceUuid !== null && enabled,
    ...DOCUMENT_QUERY
  });
}

export function useSpecificationDocument(
  specificationUuid: string | null,
  enabled: boolean
): UseQueryResult<ApiFile> {
  return useQuery({
    queryKey: ['specification-document', specificationUuid],
    queryFn: async () =>
      await httpClient.getFile(
        `/invoice_specifications/${specificationUuid ?? ''}/document`
      ),
    enabled: specificationUuid !== null && enabled,
    ...DOCUMENT_QUERY
  });
}
